import type { ReactNode, Ref } from 'react';
import { flexRender, type Row, type Table } from '@tanstack/react-table';
import {
  Table as TableRoot,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableHeader } from './sortable-header';

interface DataTableProps<T> {
  table: Table<T>;
  isLoading?: boolean;
  emptyState?: ReactNode;
  rowClassName?: (row: Row<T>, index: number) => string | undefined;
  rowRef?: (row: Row<T>) => Ref<HTMLTableRowElement> | undefined;
  onRowActivate?: (row: Row<T>) => void;
}

/** Row activation must not fire when the click landed on a control inside it. */
function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('button, a, input, [role="menuitem"]') !== null;
}

export function DataTable<T>({
  table,
  isLoading,
  emptyState,
  rowClassName,
  rowRef,
  onRowActivate,
}: DataTableProps<T>) {
  const rows = table.getRowModel().rows;
  const columnCount = table.getAllLeafColumns().length;

  return (
    <TableRoot>
      <TableHeader>
        {table.getHeaderGroups().map((group) => (
          <TableRow key={group.id}>
            {group.headers.map((header) => (
              <SortableHeader key={header.id} header={header} />
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={`skeleton-${i}`}>
              {Array.from({ length: columnCount }).map((__, j) => (
                <TableCell key={`skeleton-${i}-${j}`}>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-24 text-center text-sm text-muted-foreground">
              {emptyState}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, index) => (
            <TableRow
              key={row.id}
              ref={rowRef?.(row)}
              data-state={row.getIsSelected() ? 'selected' : undefined}
              tabIndex={onRowActivate ? 0 : undefined}
              className={`${onRowActivate ? 'cursor-pointer hover:bg-muted/50 ' : ''}${rowClassName?.(row, index) ?? ''}`}
              onClick={(e) => {
                if (!onRowActivate || isInteractive(e.target)) return;
                onRowActivate(row);
              }}
              onKeyDown={(e) => {
                if (!onRowActivate || (e.key !== 'Enter' && e.key !== ' ')) return;
                if (isInteractive(e.target)) return;
                e.preventDefault();
                onRowActivate(row);
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </TableRoot>
  );
}
